import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';
import { UploadArea } from './components/UploadArea';
import { Button } from './components/Button';
import { Features } from './components/Features';
import { HowToUse } from './components/HowToUse';
import { LanguageSelector } from './components/LanguageSelector';
import { Footer } from './components/Footer';
import { processWithBackend, downloadPageImagesZip, cleanupBackendTask } from './services/backendService';
import { Download, Sparkles, Wand2, AlertCircle, Heart, Coffee, QrCode, ChevronRight } from 'lucide-react';
import { ProcessingState } from './types';
import { Analytics } from '@vercel/analytics/react';
import { Link } from 'react-router-dom';

const FRONTEND_FALLBACK_CLEANUP_DELAY_MS = 5 * 60 * 1000;

const MainPage: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { lang } = useParams<{ lang: string }>();
  const [file, setFile] = useState<File | null>(null);
  const [processingState, setProcessingState] = useState<ProcessingState>({ status: 'idle', progress: 0 });
  const [downloadUrl, setDownloadUrl] = useState<string | null>(null);
  const [currentTaskId, setCurrentTaskId] = useState<string | null>(null);
  const [isDownloadingImages, setIsDownloadingImages] = useState(false);

  // 根据 URL 参数切换语言
  useEffect(() => {
    if (lang && ['en', 'zh', 'tw', 'ja', 'de', 'fr', 'es', 'ar', 'pt', 'id', 'ko', 'vi', 'ru'].includes(lang)) {
      const targetLang = lang === 'tw' ? 'zh-TW' : lang;
      if (i18n.language !== targetLang) {
        i18n.changeLanguage(targetLang);
      }
    } else if (!lang) {
      // 如果没有语言参数，且当前不是英语，则切换回英语
      if (i18n.language !== 'en') {
        i18n.changeLanguage('en');
      }
    }
  }, [lang, i18n]);

  const processFile = async (fileToProcess: File) => {
    setProcessingState({ status: 'processing', progress: 10 });

    try {
      // Development uses 300 DPI, Production uses 200 DPI
      // const isDev = import.meta.env.DEV;
      // const targetDpi = isDev ? 72 : 200;
      const targetDpi = 200; // 原文件是72dpi，转换为200dpi
      
      const result = await processWithBackend(
        fileToProcess,
        {
          dpi: targetDpi,
          jpeg_quality: 80,
          watermark_width: 300,
          watermark_height: 70,
        },
        (progress) => {
          setProcessingState({ status: 'processing', progress });
        }
      );

      const blob = new Blob([result.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      
      setDownloadUrl(url);
      setCurrentTaskId(result.taskId);
      setProcessingState({ status: 'complete', progress: 100 });
      
    } catch (error: any) {
      setProcessingState({ 
        status: 'error', 
        message: error.message || 'Failed to process file.', 
        progress: 0 
      });
    }
  };

  const handleDownloadImagesZip = async () => {
    if (!currentTaskId || !file) return;
    setIsDownloadingImages(true);
    try {
      const zipData = await downloadPageImagesZip(currentTaskId);
      const zipBlob = new Blob([zipData], { type: 'application/zip' });
      const zipUrl = URL.createObjectURL(zipBlob);
      const a = document.createElement('a');
      const baseName = (file.name || 'document.pdf').replace(/\.pdf$/i, '');
      a.href = zipUrl;
      a.download = `Cleaned-${baseName}-pages.zip`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(zipUrl);
    } catch (error) {
      console.error('Failed to download page images zip:', error);
      alert(t('app.downloadImagesZipError'));
    } finally {
      setIsDownloadingImages(false);
    }
  };

  const handleFileSelect = async (selectedFile: File) => {
    if (currentTaskId) {
      await cleanupBackendTask(currentTaskId);
      setCurrentTaskId(null);
    }

    setFile(selectedFile);
    setProcessingState({ status: 'idle', progress: 0 });
    if (downloadUrl) {
      URL.revokeObjectURL(downloadUrl);
    }
    setDownloadUrl(null);
    
    await processFile(selectedFile);
  };

  useEffect(() => {
    if (!currentTaskId) return;

    const timerId = window.setTimeout(() => {
      void cleanupBackendTask(currentTaskId);
    }, FRONTEND_FALLBACK_CLEANUP_DELAY_MS);

    return () => {
      window.clearTimeout(timerId);
    };
  }, [currentTaskId]);

  useEffect(() => {
    return () => {
      if (currentTaskId) {
        void cleanupBackendTask(currentTaskId);
      }
    };
  }, [currentTaskId]);

  return (
    <div className="min-h-screen w-full bg-slate-50 text-slate-900 flex flex-col relative overflow-x-hidden selection:bg-cyan-200 selection:text-cyan-900">
      {/* Background Effects */}
      <div className="fixed top-[-20%] left-[-10%] w-[500px] h-[500px] bg-cyan-200/40 rounded-full blur-[100px] pointer-events-none mix-blend-multiply" />
      <div className="fixed bottom-[-20%] right-[-10%] w-[600px] h-[600px] bg-purple-200/40 rounded-full blur-[100px] pointer-events-none mix-blend-multiply" />

      {/* Navbar */}
      <nav className="w-full py-6 px-8 flex justify-between items-center border-b border-slate-200/60 bg-white/70 backdrop-blur-md sticky top-0 z-50">
        <div className="flex items-center gap-2">
          <Link to="/" className="flex items-center gap-2 group">
            <div className="w-8 h-8 bg-gradient-to-br from-cyan-500 to-blue-600 rounded-lg flex items-center justify-center shadow-lg shadow-cyan-500/20 group-hover:shadow-cyan-500/40 transition-shadow">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <span className="text-xl font-bold font-display tracking-tight text-slate-800">SlideDeck<span className="text-cyan-600">Cleaner</span></span>
          </Link>
        </div>
        
        <LanguageSelector />
      </nav>

      <main className="flex-1 flex flex-col items-center justify-center p-6 relative z-10">
        
        <div className="text-center max-w-6xl mx-auto mt-12">          
          <h1 className="text-5xl md:text-7xl font-bold font-display mb-6 text-slate-900 leading-tight tracking-tight">
            {t('app.title')}
          </h1>
          
          <p className="text-lg text-slate-600 max-w-2xl mx-auto leading-relaxed font-medium">
            {t('app.description')}
          </p>
        </div>

        {/* Hide UploadArea when processing or complete to simplify UI */}
        {processingState.status === 'idle' && !downloadUrl && (
          <>
            <UploadArea 
              selectedFile={file} 
              onFileSelect={handleFileSelect} 
              onClear={() => {
                setFile(null);
                setProcessingState({ status: 'idle', progress: 0 });
                setDownloadUrl(null);
              }} 
            />
            <HowToUse />
          </>
        )}

        {/* Processing Progress - simplified view */}
        {processingState.status === 'processing' && (
          <div className="w-full max-w-xl mt-8 p-8 bg-white rounded-2xl border border-slate-200 shadow-xl shadow-slate-200/50 text-center">
             <div className="flex flex-col items-center justify-center mb-6">
                <div className="w-16 h-16 bg-cyan-50 rounded-full flex items-center justify-center mb-4 animate-pulse">
                  <Wand2 className="w-8 h-8 text-cyan-500 animate-spin-slow" />
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2">{t('app.processing')}</h3>
                <p className="text-slate-500">{t('app.processingDesc')}</p>
             </div>
            
            <div className="w-full bg-slate-100 rounded-full h-4 overflow-hidden relative">
              <div 
                className="bg-gradient-to-r from-cyan-500 to-blue-600 h-full transition-all duration-300 ease-out relative"
                style={{ width: `${processingState.progress}%` }}
              >
                 <div className="absolute inset-0 bg-white/20 animate-[shimmer_2s_infinite] skew-x-12"></div>
              </div>
            </div>
            <p className="mt-4 font-mono text-cyan-600 font-bold">{Math.round(processingState.progress)}%</p>
          </div>
        )}

        {/* Completion View */}
        {processingState.status === 'complete' && downloadUrl && (
          <div className="w-full max-w-xl mt-8 animate-in fade-in slide-in-from-bottom-10 duration-700">
            <div className="bg-white border border-green-100 p-8 rounded-2xl text-center relative overflow-hidden shadow-xl shadow-green-500/10">
              <div className="absolute inset-0 bg-gradient-to-b from-green-50/50 to-transparent pointer-events-none" />
              <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center mx-auto mb-4 text-green-600 rotate-3 transform">
                <Sparkles className="w-8 h-8 -rotate-3" />
              </div>
              <h3 className="text-2xl font-bold text-slate-900 mb-2">{t('app.complete')}</h3>
              <p className="text-slate-500 mb-8 font-medium">{t('app.completeDesc')}</p>
              
              <a 
                href={downloadUrl} 
                download={`Cleaned-${file?.name}`}
                className="block w-full"
              >
                <Button className="w-full !bg-gradient-to-r !from-green-500 !to-emerald-600 !border-green-400/20 !shadow-lg !shadow-green-500/30 hover:!shadow-green-500/40 py-4 text-lg">
                  {t('app.download')}
                  <Download className="w-5 h-5 ml-2" />
                </Button>
              </a>

              <button
                onClick={handleDownloadImagesZip}
                disabled={!currentTaskId || isDownloadingImages}
                className="mt-3 w-full py-3 px-4 rounded-xl font-semibold text-base border transition-all bg-emerald-50 text-emerald-700 border-emerald-300 hover:bg-emerald-100 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {isDownloadingImages ? t('app.preparingZip') : t('app.downloadImagesZip')}
              </button>

              <div className="mt-6 bg-slate-50/80 border-t border-slate-100 p-8 sm:p-10 rounded-2xl">
                <div className="text-center mb-8">
                  <div className="inline-flex items-center justify-center gap-2 px-3 py-1 bg-rose-100 text-rose-600 rounded-full text-xs font-medium mb-3">
                    <Heart className="w-3 h-3 fill-rose-500" />
                    <span>{t('app.donateBadge')}</span>
                  </div>
                  <h2 className="text-xl font-semibold text-slate-800">{t('app.donateTitle')}</h2>
                  <p className="text-slate-500 text-sm mt-2 max-w-lg mx-auto leading-relaxed">
                    {t('app.donateDesc')}
                  </p>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 animate-delay-200 animate-fade-in-up">
                  <div className="relative p-5 rounded-2xl border transition-all duration-200 group flex flex-col h-full border-amber-200 bg-amber-50 hover:border-amber-400 hover:shadow-amber-100">
                    <div className="flex items-start justify-between h-12 mb-4">
                      <div className="p-2.5 bg-white rounded-xl shadow-sm">
                        <Coffee className="w-6 h-6 text-amber-600" />
                      </div>
                      <div
                        aria-hidden="true"
                        className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-white/50 px-2 py-1 rounded opacity-0 select-none"
                      >
                        {t('app.donateWechatTag')}
                      </div>
                    </div>
                    <div className="min-h-[72px]">
                      <h3 className="font-bold text-slate-800 text-lg">{t('app.donateCoffeeTitle')}</h3>
                      <p className="text-slate-500 text-sm mt-1 mb-4">{t('app.donateCoffeeSub')}</p>
                    </div>
                    <a
                      href="https://ko-fi.com/slidedeckcleaner"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="w-full mt-auto py-2.5 px-4 rounded-xl font-medium text-sm shadow-sm border transition-all flex items-center justify-center gap-2 bg-[#FFC439] hover:bg-[#F4BB29] text-amber-900 border-amber-300"
                      onClick={() => {
                        if (typeof (window as any).gtag !== 'undefined') {
                          (window as any).gtag('event', 'donate_click', {
                            event_category: 'engagement',
                            event_label: 'ko-fi_button'
                          });
                        }
                      }}
                    >
                      {t('app.donatePaypalButton')}
                      <ChevronRight className="w-4 h-4 opacity-60" />
                    </a>
                  </div>

                  <div className="relative p-5 rounded-2xl border transition-all duration-200 group flex flex-col h-full border-emerald-200 bg-emerald-50 hover:border-emerald-400 hover:shadow-emerald-100">
                    <div className="flex items-start justify-between h-12 mb-4">
                      <div className="p-2.5 bg-white rounded-xl shadow-sm">
                        <QrCode className="w-6 h-6 text-emerald-600" />
                      </div>
                      <div className="text-[10px] font-bold uppercase tracking-wider text-slate-400 bg-white/50 px-2 py-1 rounded">
                        {t('app.donateWechatTag')}
                      </div>
                    </div>
                    <div className="min-h-[72px]">
                      <h3 className="font-bold text-slate-800 text-lg">{t('app.donateWechatTitle')}</h3>
                      <p className="text-slate-500 text-sm mt-1 mb-4">{t('app.donateWechatSub')}</p>
                    </div>
                    <div className="mt-auto pt-4 flex justify-center">
                      <img
                        src="/wechat-donate.png"
                        alt={t('app.donateQrAlt')}
                        className="w-32 h-32 rounded-lg border-2 border-emerald-200 bg-white shadow-sm"
                      />
                    </div>
                  </div>
                </div>
              </div>

              <button 
                onClick={() => {
                  if (currentTaskId) {
                    cleanupBackendTask(currentTaskId);
                  }
                  setFile(null);
                  setProcessingState({ status: 'idle', progress: 0 });
                  setCurrentTaskId(null);
                  setDownloadUrl(null);
                  if (downloadUrl) URL.revokeObjectURL(downloadUrl);
                }}
                className="mt-6 text-sm text-slate-400 hover:text-slate-600 transition-colors font-medium flex items-center justify-center gap-2 mx-auto"
              >
                <Wand2 className="w-3 h-3" />
                {t('app.cleanAnother')}
              </button>
            </div>
          </div>
        )}

        {processingState.status === 'error' && (
          <div className="w-full max-w-xl mt-6 p-6 bg-red-50 border border-red-200 text-red-700 rounded-xl flex flex-col items-center gap-3 shadow-sm text-center">
            <AlertCircle className="w-8 h-8 flex-shrink-0 text-red-500" />
            <div>
               <h4 className="font-bold text-lg mb-1">{t('app.error')}</h4>
               <p className="text-red-600/80">{processingState.message}</p>
            </div>
            <Button 
               onClick={() => {
                 setFile(null);
                 setProcessingState({ status: 'idle', progress: 0 });
               }}
               className="mt-4 !bg-white !text-red-600 !border-red-200 hover:!bg-red-50"
            >
               {t('app.tryAgain')}
            </Button>
          </div>
        )}

        <Features />
      </main>

      <Footer />
      
      {/* Vercel Analytics */}
      <Analytics />
    </div>
  );
};

export default MainPage;
